<template>
<div class="ev-root" :style="themePageStyle">

  <nav class="ev-topbar">
    <div class="ev-tb-inner">
      <button class="ev-back" @click="goBack"><i class="mdi mdi-arrow-left"></i></button>
      <a href="javascript:void(0)" @click="$router.push('/')"><img src="/assets/images/flockr_logo.png" alt="Flockr" class="ev-logo" /></a>
      <span class="ev-tb-title">Events &amp; Market Days</span>
      <button class="ev-create-btn" @click="openCreate()"><i class="mdi mdi-plus me-1"></i>Create</button>
    </div>
  </nav>

  <div class="ev-wrap">
    <!-- tabs -->
    <div class="ev-tabs">
      <button :class="{active:tab==='upcoming'}" @click="tab='upcoming';loadEvents()">Upcoming</button>
      <button :class="{active:tab==='going'}" @click="tab='going';loadEvents()">Going</button>
      <button :class="{active:tab==='mine'}" @click="tab='mine';loadEvents()">Hosting</button>
      <button :class="{active:tab==='past'}" @click="tab='past';loadEvents()">Past</button>
    </div>

    <!-- type filter -->
    <div class="ev-types">
      <button class="ev-type-chip" :class="{active:typeFilter===''}" @click="typeFilter='';loadEvents()">All</button>
      <button v-for="t in types" :key="t.key" class="ev-type-chip" :class="{active:typeFilter===t.key}" @click="typeFilter=t.key;loadEvents()">
        <i class="mdi" :class="t.icon"></i> {{ t.label }}
      </button>
    </div>

    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
    <div v-else-if="!events.length" class="ev-empty">
      <i class="mdi mdi-calendar-blank"></i>
      <h5>{{ tab==='going' ? "You haven't RSVP'd to anything yet" : tab==='mine' ? "You haven't created any events" : tab==='past' ? 'No past events' : 'No upcoming events' }}</h5>
      <p>Market days, auctions, agro shows and field days will appear here.</p>
      <button class="btn btn-warning" @click="openCreate()"><i class="mdi mdi-plus me-1"></i>Create an event</button>
    </div>

    <div v-else class="ev-grid">
      <div v-for="e in events" :key="e.id" class="ev-card" @click="openDetail(e)">
        <div class="ev-cover" :class="'t-'+e.event_type">
          <img v-if="e.cover_url" :src="mediaUrl(e.cover_url)" />
          <i v-else class="mdi" :class="typeIcon(e.event_type)"></i>
          <div class="ev-datebadge"><span class="m">{{ dateBadge(e.starts_at).mon }}</span><span class="d">{{ dateBadge(e.starts_at).day }}</span></div>
          <span class="ev-typetag">{{ typeLabel(e.event_type) }}</span>
        </div>
        <div class="ev-body">
          <div class="ev-when">{{ whenText(e.starts_at) }}</div>
          <div class="ev-title">{{ e.title }}</div>
          <div v-if="e.location_label || e.venue" class="ev-loc"><i class="mdi mdi-map-marker"></i>{{ e.venue || e.location_label }}</div>
          <div class="ev-counts">
            <span v-if="Number(e.going_count)>0"><i class="mdi mdi-account-check"></i> {{ e.going_count }} going</span>
            <span v-if="Number(e.interested_count)>0"><i class="mdi mdi-star-outline"></i> {{ e.interested_count }} interested</span>
          </div>
          <div class="ev-rsvp" @click.stop>
            <button class="ev-rsvp-btn going" :class="{on:e.my_rsvp==='going'}" @click="rsvp(e,'going')"><i class="mdi mdi-check"></i> Going</button>
            <button class="ev-rsvp-btn interested" :class="{on:e.my_rsvp==='interested'}" @click="rsvp(e,'interested')"><i class="mdi mdi-star"></i> Interested</button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ===== DETAIL MODAL ===== -->
  <div v-if="detail.open" class="ev-mod-overlay" @click.self="closeDetail">
    <div class="ev-mod-card ev-detail-card">
      <div v-if="detail.loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
      <template v-else-if="detail.event">
        <div class="ev-detail-cover" :class="'t-'+detail.event.event_type">
          <img v-if="detail.event.cover_url" :src="mediaUrl(detail.event.cover_url)" />
          <i v-else class="mdi" :class="typeIcon(detail.event.event_type)"></i>
          <button class="ev-detail-x" @click="closeDetail"><i class="mdi mdi-close"></i></button>
          <span class="ev-typetag">{{ typeLabel(detail.event.event_type) }}</span>
          <span v-if="detail.event.status==='cancelled'" class="ev-cancelled">Cancelled</span>
        </div>
        <div class="ev-detail-body">
          <div class="ev-when big">{{ whenText(detail.event.starts_at) }}<span v-if="detail.event.ends_at"> – {{ whenText(detail.event.ends_at) }}</span></div>
          <h3 class="ev-detail-title">{{ detail.event.title }}</h3>
          <div class="ev-detail-meta">
            <div v-if="detail.event.venue"><i class="mdi mdi-map-marker"></i>{{ detail.event.venue }}<span v-if="detail.event.location_label"> · {{ detail.event.location_label }}</span></div>
            <div v-else-if="detail.event.location_label"><i class="mdi mdi-map-marker"></i>{{ detail.event.location_label }}</div>
            <div class="ev-organizer"><user-avatar :src="detail.event.organizer_avatar" :name="detail.event.organizer_name" size="sm" /> Hosted by <strong>{{ detail.event.organizer_name || 'A farmer' }}</strong></div>
          </div>

          <div class="ev-detail-counts">
            <span><strong>{{ detail.event.going_count }}</strong> going</span>
            <span><strong>{{ detail.event.interested_count }}</strong> interested</span>
          </div>

          <div v-if="detail.event.status!=='cancelled'" class="ev-rsvp big">
            <button class="ev-rsvp-btn going" :class="{on:detail.event.my_rsvp==='going'}" @click="rsvpDetail('going')"><i class="mdi mdi-check"></i> Going</button>
            <button class="ev-rsvp-btn interested" :class="{on:detail.event.my_rsvp==='interested'}" @click="rsvpDetail('interested')"><i class="mdi mdi-star"></i> Interested</button>
          </div>

          <div v-if="detail.event.description" class="ev-detail-desc">{{ detail.event.description }}</div>

          <!-- engagement -->
          <div class="ev-engage">
            <div v-if="Number(detail.event.reaction_count)>0 || Number(detail.event.comment_count)>0" class="ev-engage-counts">
              <span v-if="Number(detail.event.reaction_count)>0"><i class="mdi mdi-thumb-up"></i> {{ detail.event.reaction_count }}</span>
              <span v-if="Number(detail.event.comment_count)>0" style="margin-left:auto;cursor:pointer" @click="scrollEvComments">{{ detail.event.comment_count }} comment{{ Number(detail.event.comment_count)>1?'s':'' }}</span>
            </div>
            <div class="ev-eng-actions">
              <button class="ev-eng-act" :class="{liked:detail.event.my_reaction}" @click="likeEvent"><i class="mdi" :class="detail.event.my_reaction?'mdi-thumb-up':'mdi-thumb-up-outline'"></i> Like</button>
              <button class="ev-eng-act" @click="scrollEvComments"><i class="mdi mdi-comment-outline"></i> Comment</button>
              <button class="ev-eng-act" :class="{shared:detailShared}" @click="shareEvent"><i class="mdi mdi-share-outline"></i> {{ detailShared?'Shared':'Share' }}</button>
            </div>
          </div>

          <div class="ev-comments" ref="evComments">
            <div v-if="detailReplyTo" class="ev-reply-chip">Replying to <strong>{{ detailReplyTo.author_name || 'a farmer' }}</strong><i class="mdi mdi-close" @click="detailReplyTo=null"></i></div>
            <div class="ev-comment-compose">
              <user-avatar :name="myName" :src="myAvatar" size="sm" />
              <input v-model="detailCommentInput" class="ev-comment-input" :placeholder="detailReplyTo ? 'Write a reply…' : 'Write a comment…'" @keyup.enter="postEventComment" />
              <button class="ev-comment-send" @click="postEventComment" :disabled="detailCommentBusy || !detailCommentInput.trim()"><i class="mdi mdi-send"></i></button>
            </div>
            <div v-for="c in evTopComments" :key="c.id" class="ev-comment">
              <user-avatar :src="c.author_avatar" :name="c.author_name" size="sm" />
              <div class="ev-comment-col">
                <div class="ev-comment-bubble"><div class="ev-comment-author">{{ c.author_name && c.author_name.trim() ? c.author_name : 'A farmer' }}</div><div class="ev-comment-text">{{ c.content }}</div></div>
                <div class="ev-comment-foot"><span>{{ relTimeEv(c.created_at) }}</span><button class="ev-reply-btn" @click="startEvReply(c)">Reply</button></div>
                <div v-for="r in evRepliesOf(c.id)" :key="r.id" class="ev-comment ev-reply">
                  <user-avatar :src="r.author_avatar" :name="r.author_name" size="sm" />
                  <div class="ev-comment-col">
                    <div class="ev-comment-bubble"><div class="ev-comment-author">{{ r.author_name && r.author_name.trim() ? r.author_name : 'A farmer' }}</div><div class="ev-comment-text">{{ r.content }}</div></div>
                    <div class="ev-comment-foot"><span>{{ relTimeEv(r.created_at) }}</span><button class="ev-reply-btn" @click="startEvReply(c)">Reply</button></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="detail.attendees.length" class="ev-attendees">
            <div class="ev-attendees-head">Who's going</div>
            <div class="ev-attendees-row">
              <div v-for="a in detail.attendees" :key="a.user_id" class="ev-attendee" :title="a.name + ' · ' + a.status">
                <user-avatar :src="a.avatar_url" :name="a.name" size="sm" />
              </div>
            </div>
          </div>

          <div v-if="detail.event.organizer_id===myUserId" class="ev-manage">
            <button class="ev-manage-btn" @click="openCreate(detail.event)"><i class="mdi mdi-pencil"></i> Edit</button>
            <button v-if="detail.event.status!=='cancelled'" class="ev-manage-btn warn" @click="cancelEvent(detail.event)"><i class="mdi mdi-cancel"></i> Cancel</button>
            <button class="ev-manage-btn danger" @click="deleteEvent(detail.event)"><i class="mdi mdi-delete"></i> Delete</button>
          </div>
        </div>
      </template>
    </div>
  </div>

  <!-- ===== CREATE / EDIT MODAL ===== -->
  <div v-if="form.open" class="ev-mod-overlay" @click.self="closeForm">
    <div class="ev-mod-card">
      <h5 class="ev-mod-title"><i class="mdi mdi-calendar-plus me-2"></i>{{ form.id ? 'Edit event' : 'Create event' }}</h5>

      <label v-if="!form.coverPreview" class="ev-cover-drop">
        <input type="file" accept="image/*" @change="onCoverPick" />
        <i class="mdi mdi-image-plus"></i><span>Add a cover photo (optional)</span>
      </label>
      <div v-else class="ev-cover-preview">
        <img :src="form.coverPreview" />
        <button class="ev-cover-x" @click="clearCover"><i class="mdi mdi-close"></i></button>
      </div>

      <label class="ev-lbl">Event name *</label>
      <input v-model="form.title" class="form-control ev-input" placeholder="e.g. Bomet Cattle Market Day" maxlength="200" />

      <label class="ev-lbl">Type</label>
      <select v-model="form.eventType" class="form-select ev-input">
        <option v-for="t in types" :key="t.key" :value="t.key">{{ t.label }}</option>
      </select>

      <div class="row g-2">
        <div class="col-6">
          <label class="ev-lbl">Starts *</label>
          <input type="datetime-local" v-model="form.startsAt" class="form-control ev-input" />
        </div>
        <div class="col-6">
          <label class="ev-lbl">Ends</label>
          <input type="datetime-local" v-model="form.endsAt" class="form-control ev-input" />
        </div>
      </div>

      <label class="ev-lbl">Venue</label>
      <input v-model="form.venue" class="form-control ev-input" placeholder="e.g. Bomet Livestock Market" maxlength="255" />

      <label class="ev-lbl">Town / area</label>
      <div class="ev-loc-input">
        <input v-model="form.locationLabel" class="form-control ev-input" placeholder="e.g. Bomet, Kenya" maxlength="255" />
        <button class="ev-gps-btn" type="button" @click="detectLocation" :disabled="form.locBusy" title="Use my location">
          <span v-if="form.locBusy" class="spinner-border spinner-border-sm"></span>
          <i v-else class="mdi mdi-crosshairs-gps"></i>
        </button>
      </div>

      <label class="ev-lbl">Details</label>
      <textarea v-model="form.description" class="form-control ev-input" rows="3" placeholder="What's happening? Species, fees, contacts…"></textarea>

      <div v-if="form.err" class="text-danger small mt-2">{{ form.err }}</div>
      <div class="d-flex gap-2 mt-3">
        <button class="btn btn-outline-secondary flex-fill" @click="closeForm" :disabled="form.busy">Cancel</button>
        <button class="btn btn-warning flex-fill" @click="submitForm" :disabled="form.busy || !form.title.trim() || !form.startsAt">
          <span v-if="form.busy" class="spinner-border spinner-border-sm me-1"></span><i v-else class="mdi mdi-check me-1"></i>{{ form.id ? 'Save changes' : 'Create event' }}
        </button>
      </div>
    </div>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F0EBE4;--fm-bg3:#E9E4DC;--fm-br:#E2DCD3}
html[data-theme="light"] body{background:#EBE7E1!important}
html[data-theme="light"] .ev-root{background:#EBE7E1!important;color:#1A1A1A!important}
html[data-theme="light"] .ev-topbar{background:#FFFFFF!important;border-bottom-color:#E2DCD3!important}
html[data-theme="light"] .ev-card,
html[data-theme="light"] .ev-mod-card{background:#FFFFFF!important;border-color:#E2DCD3!important}
html[data-theme="light"] .ev-input,
html[data-theme="light"] .ev-type-chip,
html[data-theme="light"] .ev-tabs button{background:#FFFFFF!important;color:#1A1A1A!important;border-color:#E2DCD3!important}
html[data-theme="light"] .ev-comment-bubble,
html[data-theme="light"] .ev-comment-input{background:#EBE7E1!important;color:#1A1A1A!important}
html[data-theme="light"] .ev-eng-act:hover{background:#EFEAE3!important}
</style>

<style scoped>
.ev-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.ev-topbar{position:sticky;top:0;z-index:100;background:var(--fm-bg1,#141414);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.ev-tb-inner{display:flex;align-items:center;gap:12px;max-width:960px;margin:0 auto;padding:10px 14px}
.ev-back{width:38px;height:38px;border-radius:50%;border:none;background:var(--fm-bg2,#0F0F0F);color:var(--fm-h,#F5F0E8);font-size:20px}
.ev-logo{height:30px}
.ev-tb-title{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:15px;flex:1}
.ev-create-btn{background:#BFA164;border:none;color:#0A0A0A;font-weight:700;font-size:13px;padding:8px 14px;border-radius:8px}
.ev-wrap{max-width:960px;margin:0 auto;padding:16px 14px 60px}

.ev-tabs{display:flex;gap:8px;margin-bottom:12px;overflow-x:auto}
.ev-tabs button{flex:1;min-width:90px;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-m,#A09888);border-radius:10px;padding:9px;font-size:13px;font-weight:700}
.ev-tabs button.active{background:#BFA164;color:#0A0A0A;border-color:#BFA164}

.ev-types{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:16px}
.ev-type-chip{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-m,#A09888);border-radius:18px;padding:6px 12px;font-size:12px;font-weight:600;display:inline-flex;align-items:center;gap:5px}
.ev-type-chip.active{background:rgba(191,161,100,.14);border-color:#BFA164;color:#BFA164}

.ev-empty{text-align:center;padding:50px 20px;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px}
.ev-empty i{font-size:60px;color:var(--fm-s,#3A3530)}
.ev-empty h5{color:var(--fm-h,#F5F0E8);margin-top:12px}
.ev-empty p{color:var(--fm-s,#6B6356);font-size:14px;margin-bottom:16px}

.ev-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:16px}
.ev-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;overflow:hidden;cursor:pointer;transition:transform .15s,border-color .15s}
.ev-card:hover{transform:translateY(-2px);border-color:#BFA164}
.ev-cover{position:relative;height:140px;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#2a2418,#14110a)}
.ev-cover img{width:100%;height:100%;object-fit:cover}
.ev-cover>.mdi{font-size:52px;color:rgba(191,161,100,.5)}
.ev-cover.t-auction{background:linear-gradient(135deg,#2a1818,#140a0a)}
.ev-cover.t-agro_show{background:linear-gradient(135deg,#182a1a,#0a140b)}
.ev-cover.t-training{background:linear-gradient(135deg,#181f2a,#0a0d14)}
.ev-cover.t-vaccination{background:linear-gradient(135deg,#2a182a,#140a14)}
.ev-datebadge{position:absolute;top:10px;left:10px;background:var(--fm-bg1,#141414);border-radius:8px;padding:4px 8px;text-align:center;min-width:42px;box-shadow:0 2px 8px rgba(0,0,0,.4)}
.ev-datebadge .m{display:block;color:#E5533C;font-size:10px;font-weight:800;text-transform:uppercase}
.ev-datebadge .d{display:block;color:var(--fm-h,#F5F0E8);font-size:17px;font-weight:800;line-height:1}
.ev-typetag{position:absolute;bottom:10px;right:10px;background:rgba(0,0,0,.6);color:#fff;font-size:10px;font-weight:700;padding:3px 8px;border-radius:6px;text-transform:capitalize}
.ev-body{padding:12px 14px}
.ev-when{color:#BFA164;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.3px}
.ev-when.big{font-size:13px}
.ev-title{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:16px;line-height:1.3;margin:4px 0}
.ev-loc{color:var(--fm-m,#A09888);font-size:12px;display:flex;align-items:center;gap:3px}
.ev-loc i{color:#66BB6A}
.ev-counts{display:flex;gap:12px;color:var(--fm-s,#6B6356);font-size:11px;margin-top:8px}
.ev-counts i{font-size:12px}
.ev-rsvp{display:flex;gap:8px;margin-top:12px}
.ev-rsvp.big{margin-top:16px}
.ev-rsvp-btn{flex:1;border:1px solid var(--fm-br,#1E1E1E);background:var(--fm-bg2,#0F0F0F);color:var(--fm-m,#A09888);border-radius:8px;padding:8px;font-size:13px;font-weight:700;display:flex;align-items:center;justify-content:center;gap:5px}
.ev-rsvp-btn.going.on{background:#45BD62;border-color:#45BD62;color:#0A0A0A}
.ev-rsvp-btn.interested.on{background:#BFA164;border-color:#BFA164;color:#0A0A0A}

/* detail */
.ev-mod-overlay{position:fixed;inset:0;background:rgba(0,0,0,.72);backdrop-filter:blur(4px);z-index:2400;display:flex;align-items:center;justify-content:center;padding:18px}
.ev-mod-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:20px;max-width:520px;width:100%;max-height:92vh;overflow-y:auto}
.ev-detail-card{padding:0;overflow:hidden}
.ev-detail-cover{position:relative;height:180px;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#2a2418,#14110a)}
.ev-detail-cover img{width:100%;height:100%;object-fit:cover}
.ev-detail-cover>.mdi{font-size:64px;color:rgba(191,161,100,.5)}
.ev-detail-x{position:absolute;top:12px;right:12px;width:34px;height:34px;border-radius:50%;background:rgba(0,0,0,.5);border:none;color:#fff;font-size:18px}
.ev-cancelled{position:absolute;top:12px;left:12px;background:#E53935;color:#fff;font-size:11px;font-weight:700;padding:3px 10px;border-radius:6px}
.ev-detail-body{padding:18px}
.ev-detail-title{color:var(--fm-h,#F5F0E8);font-weight:800;font-size:22px;line-height:1.25;margin:4px 0 10px}
.ev-detail-meta div{color:var(--fm-m,#A09888);font-size:13px;display:flex;align-items:center;gap:6px;margin-bottom:6px}
.ev-detail-meta i{color:#66BB6A;font-size:16px}
.ev-organizer{margin-top:4px}
.ev-detail-counts{display:flex;gap:18px;margin:14px 0;color:var(--fm-m,#A09888);font-size:13px}
.ev-detail-counts strong{color:var(--fm-h,#F5F0E8)}
.ev-detail-desc{color:var(--fm-h,#F5F0E8);font-size:14px;line-height:1.6;white-space:pre-wrap;margin-top:14px;padding-top:14px;border-top:1px solid var(--fm-br,#1E1E1E)}
.ev-attendees{margin-top:16px}
.ev-attendees-head{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:14px;margin-bottom:8px}
.ev-attendees-row{display:flex;flex-wrap:wrap;gap:6px}
.ev-engage{margin-top:16px;padding-top:10px;border-top:1px solid var(--fm-br,#1E1E1E)}
.ev-engage-counts{display:flex;align-items:center;gap:6px;font-size:13px;color:var(--fm-s,#6B6356);padding-bottom:8px}
.ev-engage-counts i{color:#fff;border-radius:50%;font-size:10px;width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;background:linear-gradient(#18AFFF,#0062DF)}
.ev-eng-actions{display:flex;gap:4px;border-top:1px solid var(--fm-br,#1E1E1E);padding-top:6px}
.ev-eng-act{flex:1;background:transparent;border:none;color:var(--fm-m,#A09888);font-size:14px;font-weight:700;padding:9px;border-radius:8px;display:flex;align-items:center;justify-content:center;gap:8px}
.ev-eng-act:hover{background:var(--fm-bg2,#0F0F0F)}
.ev-eng-act.liked{color:#1877F2}
.ev-eng-act.shared{color:#45BD62}
.ev-eng-act i{font-size:20px}
.ev-comments{margin-top:14px}
.ev-comment-compose{display:flex;align-items:center;gap:8px;margin-bottom:14px}
.ev-comment-input{flex:1;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:20px;padding:9px 14px;color:var(--fm-h,#F5F0E8);font-size:14px;outline:none}
.ev-comment-input:focus{border-color:#BFA164}
.ev-comment-send{background:#BFA164;border:none;color:#0A0A0A;border-radius:50%;width:36px;height:36px;font-size:18px;flex-shrink:0}
.ev-comment-send:disabled{opacity:.4}
.ev-comment{display:flex;gap:8px;margin-bottom:12px}
.ev-comment-col{flex:1;min-width:0}
.ev-comment-bubble{background:var(--fm-bg2,#0F0F0F);border-radius:16px;padding:8px 12px;display:inline-block;max-width:100%}
.ev-comment-author{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:13px}
.ev-comment-text{color:var(--fm-h,#F5F0E8);font-size:14px;white-space:pre-wrap}
.ev-comment-time{color:var(--fm-s,#6B6356);font-size:11px;margin:3px 0 0 12px}
.ev-comment-foot{display:flex;align-items:center;gap:14px;margin:3px 0 0 12px;font-size:11px;color:var(--fm-s,#6B6356)}
.ev-reply-btn{background:none;border:none;color:var(--fm-s,#6B6356);font-weight:700;font-size:12px;padding:0;cursor:pointer}
.ev-reply-btn:hover{color:#BFA164}
.ev-reply{margin-top:10px;margin-left:14px}
.ev-reply-chip{display:inline-flex;align-items:center;gap:6px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:4px 10px;font-size:12px;color:var(--fm-m,#A09888);margin-bottom:8px}
.ev-reply-chip i{cursor:pointer;font-size:15px}
.ev-reply-chip i:hover{color:#FF6B6B}
.ev-manage{display:flex;gap:8px;margin-top:18px;padding-top:14px;border-top:1px solid var(--fm-br,#1E1E1E)}
.ev-manage-btn{flex:1;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-m,#A09888);border-radius:8px;padding:9px;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;gap:5px}
.ev-manage-btn.warn{color:#FFB300}
.ev-manage-btn.danger{color:#FF6B6B}

/* form */
.ev-mod-title{color:var(--fm-h,#F5F0E8);font-size:18px;font-weight:700;margin-bottom:12px}
.ev-lbl{display:block;color:var(--fm-h,#F5F0E8);font-size:12px;font-weight:600;margin:12px 0 4px}
.ev-input{background:var(--fm-bg2,#0F0F0F)!important;border:1px solid var(--fm-br,#1E1E1E)!important;color:var(--fm-h,#F5F0E8)!important;font-size:14px}
.ev-input:focus{border-color:#BFA164!important;box-shadow:0 0 0 2px rgba(191,161,100,.15)!important}
.ev-cover-drop{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;height:130px;border:2px dashed var(--fm-br,#1E1E1E);border-radius:12px;background:var(--fm-bg2,#0F0F0F);color:var(--fm-s,#6B6356);cursor:pointer;width:100%}
.ev-cover-drop:hover{border-color:#BFA164;color:#BFA164}
.ev-cover-drop i{font-size:40px}
.ev-cover-drop input{display:none}
.ev-cover-preview{position:relative;border-radius:12px;overflow:hidden;height:160px}
.ev-cover-preview img{width:100%;height:100%;object-fit:cover}
.ev-cover-x{position:absolute;top:8px;right:8px;width:30px;height:30px;border-radius:50%;background:rgba(0,0,0,.6);border:none;color:#fff;font-size:16px}
.ev-loc-input{display:flex;gap:8px}
.ev-gps-btn{flex-shrink:0;width:42px;border:1px solid var(--fm-br,#1E1E1E);background:var(--fm-bg2,#0F0F0F);color:#BFA164;border-radius:8px;font-size:18px}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .ev-container { padding-top: 70px !important; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 12px !important; padding-right: 12px !important; }
  .ev-card { padding: 12px !important; border-radius: 10px !important; }
  .ev-cover-preview { height: 140px; }
  .ev-gps-btn { width: 38px; font-size: 16px; }
}
</style>

<script>
/* eslint-disable */
import { execute, executeJson, flocker, uploads } from "@/api";
import UserAvatar from "@/components/flockr-users/UserAvatar.vue";

function compressImage(file, maxDim, quality){
  return new Promise(function(resolve){
    var reader = new FileReader();
    reader.onload = function(e){
      var img = new Image();
      img.onload = function(){
        var w = img.width, h = img.height;
        if (w > maxDim || h > maxDim) { if (w > h) { h = Math.round(h*maxDim/w); w = maxDim; } else { w = Math.round(w*maxDim/h); h = maxDim; } }
        var canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob(function(blob){
          if (!blob) { resolve({ file: file, preview: e.target.result }); return; }
          var emit = function(b){ var name = file.name.replace(/\.[^.]+$/, '.jpg'); var f = new File([b], name, { type:'image/jpeg' }); resolve({ file:f, preview:URL.createObjectURL(f) }); };
          if (blob.size > 2*1024*1024) canvas.toBlob(function(b2){ emit(b2||blob); }, 'image/jpeg', 0.6); else emit(blob);
        }, 'image/jpeg', quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default {
  name: "EventsPage",
  components: { UserAvatar },
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null, loading:false,
    events: [], tab:'upcoming', typeFilter:'',
    types: [
      { key:'market_day', label:'Market Day', icon:'mdi-cow' },
      { key:'auction', label:'Auction', icon:'mdi-gavel' },
      { key:'agro_show', label:'Agro Show', icon:'mdi-tractor' },
      { key:'training', label:'Training', icon:'mdi-school' },
      { key:'field_day', label:'Field Day', icon:'mdi-sprout' },
      { key:'vaccination', label:'Vaccination', icon:'mdi-needle' },
      { key:'sale', label:'Sale', icon:'mdi-tag' },
      { key:'other', label:'Other', icon:'mdi-calendar-star' }
    ],
    detail: { open:false, loading:false, event:null, attendees:[] },
    detailComments: [], detailCommentInput: '', detailCommentBusy: false, detailShared: false, detailReplyTo: null,
    form: { open:false, id:null, title:'', eventType:'market_day', startsAt:'', endsAt:'', venue:'', locationLabel:'', locationLat:null, locationLng:null, description:'', coverFile:null, coverPreview:null, locBusy:false, busy:false, err:'' }
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#EBE7E1;min-height:100vh;color:#1A1A1A;'},
    myUserId(){return sessionStorage.getItem('userId')||''},
    myName(){return sessionStorage.getItem('userName')||'You'},
    myAvatar(){return sessionStorage.getItem('userAvatar')||''},
    evTopComments(){return (this.detailComments||[]).filter(function(c){return !c.parent_comment_id;});}
  },
  watch:{ '$route.params.id'(v){ if(v) this.openDetailById(v); } },
  mounted(){
    this._applyThemeOnce();
    var self=this;
    this.themeObserver=new MutationObserver(function(){try{self._applyThemeOnce();}catch(e){}});
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.loadEvents();
    if(this.$route.params.id) this.openDetailById(this.$route.params.id);
  },
  beforeUnmount(){if(this.themeObserver){this.themeObserver.disconnect();this.themeObserver=null}},
  methods:{
    _applyThemeOnce(){
      var current=document.documentElement.getAttribute('data-theme');
      var desired=(localStorage.getItem('flockr_theme')||current||'dark');
      if(current!==desired) document.documentElement.setAttribute('data-theme',desired);
      this.darkMode=desired!=='light';
    },
    goBack(){ if(window.history.length>1) this.$router.go(-1); else this.$router.push('/forum'); },
    mediaUrl(u){ if(!u) return ''; if(u.indexOf('http')===0) return u; return uploads + String(u).replace(/^uploads\//,''); },
    typeIcon(t){ var f=this.types.find(function(x){return x.key===t;}); return f?f.icon:'mdi-calendar-star'; },
    typeLabel(t){ var f=this.types.find(function(x){return x.key===t;}); return f?f.label:'Event'; },
    _d(ts){ return ts ? new Date(String(ts).replace(' ','T')) : null; },
    dateBadge(ts){ var d=this._d(ts); if(!d) return {mon:'',day:''}; return { mon:d.toLocaleDateString('en',{month:'short'}), day:d.getDate() }; },
    whenText(ts){
      var d=this._d(ts); if(!d) return '';
      return d.toLocaleDateString('en',{weekday:'short',day:'numeric',month:'short'}) + ' · ' + d.toLocaleTimeString('en',{hour:'numeric',minute:'2-digit'});
    },
    async loadEvents(){
      this.loading=true;
      var when = this.tab==='past' ? 'past' : (this.tab==='going'||this.tab==='mine' ? 'all' : 'upcoming');
      var scope = this.tab==='going' ? 'going' : (this.tab==='mine' ? 'mine' : 'all');
      try{
        var r=await executeJson({function:'getEvents',userId:this.myUserId,when:when,scope:scope,eventType:this.typeFilter,limit:50},flocker);
        if(r.data&&r.data.success) this.events=(r.data.data&&r.data.data.events)||[];
      }catch(e){console.error(e)}
      this.loading=false;
    },
    async rsvp(e,status){
      if(!this.myUserId){this.$router.push('/login?redirect=/events');return}
      var next = e.my_rsvp===status ? 'none' : status;
      try{
        var r=await executeJson({function:'rsvpEvent',userId:this.myUserId,eventId:e.id,status:next},flocker);
        if(r.data&&r.data.success){ e.my_rsvp=r.data.data.myRsvp; e.going_count=r.data.data.goingCount; e.interested_count=r.data.data.interestedCount; }
      }catch(err){}
    },
    async openDetail(e){
      this.detail={open:true,loading:true,event:null,attendees:[]};
      await this._loadDetail(e.id);
    },
    async openDetailById(id){
      this.detail={open:true,loading:true,event:null,attendees:[]};
      await this._loadDetail(id);
    },
    async _loadDetail(id){
      this.detailComments=[]; this.detailCommentInput=''; this.detailShared=false; this.detailReplyTo=null;
      try{
        var r=await executeJson({function:'getEventDetail',eventId:id,userId:this.myUserId},flocker);
        if(r.data&&r.data.success){ this.detail.event=r.data.data.event; this.detail.attendees=(r.data.data.attendees)||[]; this.loadEventComments(id); }
      }catch(e){}
      this.detail.loading=false;
    },
    relTimeEv(ts){
      if(!ts) return '';
      var diff=Math.floor((Date.now()-new Date(String(ts).replace(' ','T')).getTime())/1000);
      if(diff<60) return 'just now';
      if(diff<3600) return Math.floor(diff/60)+'m';
      if(diff<86400) return Math.floor(diff/3600)+'h';
      if(diff<2592000) return Math.floor(diff/86400)+'d';
      return String(ts).split(' ')[0];
    },
    async loadEventComments(id){
      try{var r=await executeJson({function:'getContentComments',targetType:'event',targetId:id},flocker);
        if(r.data&&r.data.success)this.detailComments=(r.data.data&&r.data.data.comments)||[];}catch(e){}
    },
    async likeEvent(){
      if(!this.myUserId){this.$router.push('/login?redirect=/events');return}
      var e=this.detail.event;
      try{var r=await executeJson({function:'reactToPost',userId:this.myUserId,targetType:'event',targetId:e.id,reaction:'like'},flocker);
        if(r.data&&r.data.success){ e.reaction_count=r.data.data.reactionCount; e.my_reaction=r.data.data.myReaction; }}catch(err){}
    },
    scrollEvComments(){ var self=this; this.$nextTick(function(){ if(self.$refs.evComments) self.$refs.evComments.scrollIntoView({behavior:'smooth',block:'start'}); }); },
    evRepliesOf(id){ return (this.detailComments||[]).filter(function(c){return String(c.parent_comment_id)===String(id);}); },
    startEvReply(c){ this.detailReplyTo=c; this.scrollEvComments(); },
    async postEventComment(){
      var txt=(this.detailCommentInput||'').trim(); if(!txt)return;
      if(!this.myUserId){this.$router.push('/login?redirect=/events');return}
      this.detailCommentBusy=true;
      var pid = this.detailReplyTo ? this.detailReplyTo.id : 0;
      try{var r=await executeJson({function:'addContentComment',authorId:this.myUserId,targetType:'event',targetId:this.detail.event.id,content:txt,parentId:pid},flocker);
        if(r.data&&r.data.success){ this.detailCommentInput=''; this.detailReplyTo=null; this.detail.event.comment_count=r.data.data.commentCount; await this.loadEventComments(this.detail.event.id); }}catch(e){}
      this.detailCommentBusy=false;
    },
    async shareEvent(){
      if(!this.myUserId){this.$router.push('/login?redirect=/events');return}
      var e=this.detail.event;
      var note='📅 '+e.title+(e.location_label?(' — '+e.location_label):'')+'\n\nJoin me at this event on Flockr: /events/'+e.id;
      try{var r=await executeJson({function:'createFeedPost',authorId:this.myUserId,content:note,category:'general'},flocker);
        if(r.data&&r.data.success)this.detailShared=true;}catch(e2){}
    },
    closeDetail(){ this.detail.open=false; if(this.$route.params.id) this.$router.replace('/events'); },
    async rsvpDetail(status){
      if(!this.myUserId){this.$router.push('/login?redirect=/events');return}
      var e=this.detail.event; var next = e.my_rsvp===status ? 'none' : status;
      try{
        var r=await executeJson({function:'rsvpEvent',userId:this.myUserId,eventId:e.id,status:next},flocker);
        if(r.data&&r.data.success){ e.my_rsvp=r.data.data.myRsvp; e.going_count=r.data.data.goingCount; e.interested_count=r.data.data.interestedCount; this._loadDetail(e.id); }
      }catch(err){}
    },
    openCreate(ev){
      if(!this.myUserId){this.$router.push('/login?redirect=/events');return}
      if(ev){
        this.form={ open:true, id:ev.id, title:ev.title||'', eventType:ev.event_type||'market_day',
          startsAt:this._toLocalInput(ev.starts_at), endsAt:this._toLocalInput(ev.ends_at), venue:ev.venue||'',
          locationLabel:ev.location_label||'', locationLat:ev.location_lat||null, locationLng:ev.location_lng||null,
          description:ev.description||'', coverFile:null, coverPreview:ev.cover_url?this.mediaUrl(ev.cover_url):null, locBusy:false, busy:false, err:'' };
      } else {
        this.form={ open:true, id:null, title:'', eventType:'market_day', startsAt:'', endsAt:'', venue:'', locationLabel:'', locationLat:null, locationLng:null, description:'', coverFile:null, coverPreview:null, locBusy:false, busy:false, err:'' };
      }
    },
    _toLocalInput(ts){ if(!ts) return ''; return String(ts).replace(' ','T').slice(0,16); },
    closeForm(){ if(this.form.coverPreview && this.form.coverPreview.indexOf('blob:')===0) URL.revokeObjectURL(this.form.coverPreview); this.form.open=false; },
    async onCoverPick(e){
      var f=(e.target.files||[])[0]; e.target.value='';
      if(!f) return;
      if(f.type.indexOf('image/')!==0){ this.form.err='Cover must be an image'; return; }
      var c=await compressImage(f,1280,0.8); this.form.coverFile=c.file; this.form.coverPreview=c.preview; this.form.err='';
    },
    clearCover(){ if(this.form.coverPreview && this.form.coverPreview.indexOf('blob:')===0) URL.revokeObjectURL(this.form.coverPreview); this.form.coverFile=null; this.form.coverPreview=null; },
    detectLocation(){
      var self=this;
      if(!navigator.geolocation){ this.form.err='Geolocation not supported'; return; }
      this.form.locBusy=true; this.form.err='';
      navigator.geolocation.getCurrentPosition(async function(pos){
        self.form.locationLat=pos.coords.latitude; self.form.locationLng=pos.coords.longitude;
        try{
          var url='https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat='+self.form.locationLat+'&lon='+self.form.locationLng+'&zoom=10';
          var resp=await fetch(url,{headers:{'Accept-Language':'en'}}); var data=await resp.json(); var a=data.address||{};
          var parts=[a.city||a.town||a.village||a.county||a.state_district||a.state, a.country];
          self.form.locationLabel=parts.filter(Boolean).join(', ') || self.form.locationLabel;
        }catch(e){}
        self.form.locBusy=false;
      }, function(){ self.form.locBusy=false; self.form.err='Could not detect location'; }, {timeout:8000});
    },
    async submitForm(){
      if(!this.form.title.trim() || !this.form.startsAt) return;
      this.form.busy=true; this.form.err='';
      try{
        var fd=new FormData();
        fd.append('function', this.form.id ? 'updateEvent' : 'createEvent');
        if(this.form.id){ fd.append('userId', this.myUserId); fd.append('eventId', this.form.id); }
        else fd.append('organizerId', this.myUserId);
        fd.append('title', this.form.title);
        fd.append('eventType', this.form.eventType);
        fd.append('startsAt', this.form.startsAt);
        fd.append('endsAt', this.form.endsAt||'');
        fd.append('venue', this.form.venue||'');
        fd.append('locationLabel', this.form.locationLabel||'');
        if(this.form.locationLat!=null) fd.append('locationLat', this.form.locationLat);
        if(this.form.locationLng!=null) fd.append('locationLng', this.form.locationLng);
        fd.append('description', this.form.description||'');
        if(this.form.coverFile) fd.append('cover', this.form.coverFile);
        var r=await execute(fd, flocker);
        if(r.data&&r.data.success){
          this.closeForm();
          if(this.detail.open && this.detail.event) await this._loadDetail(this.detail.event.id);
          await this.loadEvents();
        }else{ this.form.err=(r.data&&r.data.message)||'Could not save event'; this.form.busy=false; }
      }catch(e){ this.form.err='Network error'; this.form.busy=false; }
      this.form.busy=false;
    },
    async cancelEvent(e){
      if(!confirm('Cancel this event? Everyone who RSVP\'d will be notified.')) return;
      try{ var r=await executeJson({function:'cancelEvent',userId:this.myUserId,eventId:e.id},flocker);
        if(r.data&&r.data.success){ e.status='cancelled'; this.loadEvents(); } }catch(err){}
    },
    async deleteEvent(e){
      if(!confirm('Delete this event permanently?')) return;
      try{ var r=await executeJson({function:'deleteEvent',userId:this.myUserId,eventId:e.id},flocker);
        if(r.data&&r.data.success){ this.closeDetail(); this.loadEvents(); } }catch(err){}
    }
  }
};
</script>
